/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { XdpV1_Org_Coursera_Degree_Stored_Api_UpsellType as UpsellTypes } from '__generated__/graphql-types';

import { useLocation, useParams, useRoutes } from 'js/lib/useRouter';

import { Divider, breakpoints, useMediaQuery } from '@coursera/cds-core';

import type { UniversityProgramDeadlines } from 'bundles/degree-description/__generated__/baseContentfulTypes';
import CareerOutcomesModule from 'bundles/degree-description/components/modules/career-outcomes/CareerOutcomesModule';
import CareerModule from 'bundles/degree-description/components/modules/career-section/CareerModule';
import EventsSection from 'bundles/degree-description/components/modules/events/EventsSection';
import EoiInlineModule from 'bundles/degree-description/components/modules/expression-of-interest/EoiInlineModule';
import FaqModule from 'bundles/degree-description/components/modules/faqs/FaqModule';
import HelpCenterBlock from 'bundles/degree-description/components/modules/faqs/HelpCenterBlock';
import FCNDModule from 'bundles/degree-description/components/modules/fcnd/FCNDModule';
import FinanceSectionModule from 'bundles/degree-description/components/modules/finance/FinanceSectionModule';
import AtAGlanceHorizontalBlock from 'bundles/degree-description/components/modules/hero/AtAGlanceHorizontalBlock';
import DeadlinesBlock from 'bundles/degree-description/components/modules/hero/DeadlinesBlock';
import EoiAtAGlanceBlock from 'bundles/degree-description/components/modules/hero/EoiAtAGlanceBlock';
import EoiButtonsBlock from 'bundles/degree-description/components/modules/hero/EoiButtonsBlock';
import HeroBanner from 'bundles/degree-description/components/modules/hero/HeroBanner';
import HeroModule from 'bundles/degree-description/components/modules/hero/HeroModule';
import InformationalSectionContainer from 'bundles/degree-description/components/modules/hero/InformationalSectionContainer';
import InfoSectionModule from 'bundles/degree-description/components/modules/info-section/InfoSectionModule';
import PersonalizedEmbeddedMsg from 'bundles/degree-description/components/modules/personalization/PersonalizedEmbeddedMsg';
import RelatedNewsModule from 'bundles/degree-description/components/modules/related-news/RelatedNewsModule';
import SchemaMarkup from 'bundles/degree-description/components/modules/structured-data-markup/SchemaMarkup';
import Testimonials from 'bundles/degree-description/components/modules/testimonials/TestimonialsModule';
import UpsellsAboutModule from 'bundles/degree-description/components/modules/upsells/UpsellsAboutModule';
import UpsellsModule from 'bundles/degree-description/components/modules/upsells/UpsellsModule';
import { HideOnDesktop, HideOnMobile } from 'bundles/degree-description/components/shared/BreakpointDisplay';
import ModuleContainerStyleWrap from 'bundles/degree-description/components/shared/ModuleContainerStyleWrap';
import InlineNavigation from 'bundles/degree-description/components/shared/Navigation/InlineNavigation';
import RichText from 'bundles/degree-description/components/shared/RichText';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import type { PropsFromDegreePageContent } from 'bundles/degree-description/pages/consumer/degree-description-page/DegreePageContent';
import LightweightPageContent from 'bundles/degree-description/pages/consumer/degree-description-page/subpages/LightweightPageContent';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { moveStackabilityModule } from 'bundles/degree-description/utils/experiments';
import { getMessagesToDisplay } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import { PAGE_NAMES_MARKUP } from 'bundles/degree-description/utils/schema-markup';
import {
  filterAndSliceStackableItemsByUpsellType,
  filterOldEventsByDate,
  getNavigationItems,
} from 'bundles/degree-description/utils/utils';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import getDegreesConversionCopy from 'bundles/stackability-common/copy/degreesConversionCopy';

import _t from 'i18n!nls/degree-description';

type Props = PropsFromDegreePageContent;

// Try to keep styling within modules and avoid adding styles to subpages as much as possible
const styles = {
  mobileOverviewAbout: css`
    padding: var(--cds-spacing-200);
  `,
};

const OverviewPageContent = ({
  pageContent,
  eoiContainerText,
  eoiButtons,
  setEoiTrackingIndex,
  personalizedDdpData,
  isLightweightDdp,
  stackableItemsWithUpsellType,
}: Props) => {
  const {
    overviewAbout,
    backgroundImage,
    partner,
    programName,
    atAGlance,
    faqCollection,
    careerSection,
    latestNews,
    upcomingEventsCollection,
    testimonialsCollection,
    financeSection,
    upsellsAbout,
    overviewValueProp,
    overviewValueProp2,
    deadlineSectionCollection,
    redesign2026Modules,
  } = pageContent;

  const { experimentValues } = useDegreeDescriptionPageData();

  // Page Sections
  const overviewText = overviewValueProp?.json;
  const overviewImage = overviewValueProp?.links?.assets?.block?.[0];

  const overviewText2 = overviewValueProp2?.json;
  const overviewImage2 = overviewValueProp2?.links?.assets?.block?.[0];

  // Page Section Tracking
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const pageHeaderTrackingRef = useGetViewPageSectionRef('page_header');

  const partOfUpsells = filterAndSliceStackableItemsByUpsellType(stackableItemsWithUpsellType, UpsellTypes.PartOf);
  const offersCreditUpsells = filterAndSliceStackableItemsByUpsellType(
    stackableItemsWithUpsellType,
    UpsellTypes.OffersCredit
  );
  const relatedToUpsells = filterAndSliceStackableItemsByUpsellType(
    stackableItemsWithUpsellType,
    UpsellTypes.RelatedTo
  );

  const offersCreditUpsellsExist = offersCreditUpsells && offersCreditUpsells.length > 0;
  const partOfUpsellsExist = partOfUpsells && partOfUpsells.length > 0;
  const relatedToUpsellsExist = relatedToUpsells && relatedToUpsells.length > 0;
  const upsellsExist = offersCreditUpsellsExist || partOfUpsellsExist || relatedToUpsellsExist;

  const overviewText2Exists =
    overviewText2 && overviewImage2 && overviewImage2?.url && overviewImage2?.width && overviewImage2?.height;

  const filteredEvents = filterOldEventsByDate(upcomingEventsCollection?.items, 7);

  const location = useLocation();
  const { slug } = useParams();
  const routes = useRoutes();

  // Personalization
  const messagesToDisplay = getMessagesToDisplay(
    personalizedDdpData,
    slug,
    programName,
    partner?.partnerName,
    deadlineSectionCollection?.items as UniversityProgramDeadlines[]
  );

  const upsellsModuleCopy = getDegreesConversionCopy().stackabilityModuleRefresh.upsellsModuleCopy;
  /**
   * TODO: Setting this boolean to false instead of completely removing because we plan on
   * making the FCND module self-serve in contentful in Q1 2026
   * https://coursera.atlassian.net/browse/MERCH-2446
   */
  const showFCNDModule = false;
  const showUpsellsAboutModule = !showFCNDModule;
  const showUpsellsModule = offersCreditUpsellsExist || partOfUpsellsExist;
  const shouldMoveStackabilityModule = moveStackabilityModule();
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  const upsellsModule = showUpsellsModule && (
    <ModuleContainerStyleWrap sectionId="takeACourse">
      {offersCreditUpsellsExist && (
        <UpsellsModule
          title={upsellsModuleCopy.offersCredit.title}
          description={upsellsModuleCopy.offersCredit.description}
          disclaimer={upsellsModuleCopy.offersCredit.disclaimer}
          upsells={offersCreditUpsells}
          trackingNamePrefix="offers_credit"
          trackingIndex={1}
        />
      )}

      {partOfUpsellsExist && (
        <>
          {offersCreditUpsellsExist && <Divider />}

          <UpsellsModule
            title={upsellsModuleCopy.partOf.title}
            description={upsellsModuleCopy.partOf.description}
            disclaimer={upsellsModuleCopy.partOf.disclaimer}
            upsells={partOfUpsells}
            trackingNamePrefix="part_of"
            trackingIndex={2}
          />
        </>
      )}
    </ModuleContainerStyleWrap>
  );

  if (isLightweightDdp) {
    return (
      <LightweightPageContent
        pageContent={pageContent}
        eoiContainerText={eoiContainerText}
        eoiButtons={eoiButtons}
        setEoiTrackingIndex={setEoiTrackingIndex}
      />
    );
  } else {
    return (
      <div className="rc-OverviewPageContent">
        <SchemaMarkup pageName={PAGE_NAMES_MARKUP.overview} pathName={location.pathname} pageContent={pageContent} />
        <HeroModule
          informationalSection={
            <HideOnMobile>
              <InformationalSectionContainer
                content={
                  <div ref={pageHeaderTrackingRef}>
                    <RichText
                      content={overviewAbout?.json}
                      contentLinks={overviewAbout?.links}
                      linkTrackingName="overviewAbout_body_link"
                      buttonTrackingNameV3="degree_info_link"
                      trackingSection="page_header"
                    />
                  </div>
                }
                sidebarContent={<DeadlinesBlock finalDeadlineInfo={atAGlance?.finalDeadlineInfo} />}
              />
            </HideOnMobile>
          }
          heroBanner={
            <HeroBanner
              backgroundImageUrl={backgroundImage?.url ?? ''}
              partnerLogoUrl={partner?.partnerLogo?.url ?? ''}
              partnerName={partner?.partnerName ?? ''}
              programName={programName ?? ''}
              isOverviewPage
              sidebarContent={
                <EoiButtonsBlock
                  eoiContainerText={eoiContainerText}
                  eoiButtons={eoiButtons}
                  trackingIndex={1}
                  setEoiTrackingIndex={setEoiTrackingIndex}
                />
              }
            />
          }
          contentInBetween={
            <>
              <AtAGlanceHorizontalBlock atAGlance={atAGlance} />
              <InlineNavigation
                navigationItems={getNavigationItems()}
                // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                parentNavRoute={routes[routes.length - 2].name}
                // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                currentNavRoute={routes[routes.length - 1].name}
                // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                slug={slug}
              />
            </>
          }
        />
        {!isMobile && shouldMoveStackabilityModule && upsellsModule}
        <HideOnDesktop>
          <>
            <EoiAtAGlanceBlock
              mobileOnly
              atAGlance={atAGlance}
              eoiContainerText={eoiContainerText}
              eoiButtons={eoiButtons}
              trackingIndex={1}
            />
            <div css={styles.mobileOverviewAbout} ref={pageHeaderTrackingRef}>
              <RichText
                content={overviewAbout?.json}
                contentLinks={overviewAbout?.links}
                linkTrackingName="overviewAbout_body_link"
                buttonTrackingNameV3="degree_info_link"
                trackingSection="page_header"
              />
            </div>
            {shouldMoveStackabilityModule && upsellsModule}

            {messagesToDisplay.embeddedMessageToDisplay && (
              <ModuleContainerStyleWrap blueBackground>
                <PersonalizedEmbeddedMsg
                  personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
                  eoiButtons={eoiButtons}
                  trackingIndex={2}
                  setEoiTrackingIndex={setEoiTrackingIndex}
                />
              </ModuleContainerStyleWrap>
            )}
            <DeadlinesBlock finalDeadlineInfo={atAGlance?.finalDeadlineInfo} />
          </>
        </HideOnDesktop>
        <HideOnMobile>
          <>
            {messagesToDisplay.embeddedMessageToDisplay && (
              <ModuleContainerStyleWrap blueBackground>
                <PersonalizedEmbeddedMsg
                  personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
                  eoiButtons={eoiButtons}
                  trackingIndex={2}
                  setEoiTrackingIndex={setEoiTrackingIndex}
                />
              </ModuleContainerStyleWrap>
            )}
          </>
        </HideOnMobile>

        {overviewText && overviewImage && overviewImage?.url && overviewImage?.width && overviewImage?.height && (
          <ModuleContainerStyleWrap>
            <InfoSectionModule
              description={
                <RichText
                  content={overviewText}
                  linkTrackingName="overviewValueProp1_info_link"
                  buttonTrackingNameV3="degree_info_link"
                  trackingSection="value_proposition"
                  trackingIndex={1}
                />
              }
              trackingIndex={1}
              media={
                <LazyImgix
                  src={overviewImage.url}
                  maxWidth={overviewImage.width}
                  maxHeight={overviewImage.height}
                  alt={overviewImage.description || ''}
                  enableFluidWidth={true}
                />
              }
            />
          </ModuleContainerStyleWrap>
        )}
        {financeSection && (
          <ModuleContainerStyleWrap>
            <FinanceSectionModule
              title={financeSection?.title || ''}
              markdownContent={financeSection?.description || ''}
              ctaLink={financeSection?.callToActionLink || ''}
              ctaText={financeSection?.callToActionLabel || ''}
            />
          </ModuleContainerStyleWrap>
        )}
        {experimentValues?.redesign2026Variant &&
          experimentValues.redesign2026Variant !== 'control' &&
          redesign2026Modules?.careerOutcomes && (
            <ModuleContainerStyleWrap>
              <CareerOutcomesModule
                heading={redesign2026Modules?.careerOutcomes?.heading}
                valueProps={redesign2026Modules?.careerOutcomes?.valuePropsCollection?.items}
                rolesTitle={redesign2026Modules?.careerOutcomes?.rolesHeading}
                roles={redesign2026Modules?.careerOutcomes?.rolesCollection?.items}
                logosTitle={redesign2026Modules?.careerOutcomes?.logosHeading}
                logos={redesign2026Modules?.careerOutcomes?.whereGraduatesWorkLogosCollection?.items}
              />
            </ModuleContainerStyleWrap>
          )}
        {testimonialsCollection && (
          <ModuleContainerStyleWrap>
            <Testimonials
              testimonials={testimonialsCollection}
              applicationUrl={pageContent?.expressionOfInterest?.applyButtonUrl}
              eoiButtons={eoiButtons}
              // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
              slug={slug}
            />
          </ModuleContainerStyleWrap>
        )}
        {careerSection && (
          <ModuleContainerStyleWrap>
            <CareerModule careerSection={careerSection} trackingSection="career_info" />
          </ModuleContainerStyleWrap>
        )}
        {overviewText2 && overviewImage2 && overviewImage2?.url && overviewImage2?.width && overviewImage2?.height && (
          <ModuleContainerStyleWrap blueBackground>
            <InfoSectionModule
              textColor="var(--cds-color-white-0)"
              description={
                <RichText
                  content={overviewText2}
                  linkTrackingName="overviewValueProp2_info_link"
                  buttonTrackingNameV3="degree_info_link"
                  trackingSection="value_proposition"
                  trackingIndex={2}
                />
              }
              trackingIndex={2}
              media={
                <LazyImgix
                  src={overviewImage2.url}
                  maxWidth={overviewImage2.width}
                  maxHeight={overviewImage2.height}
                  alt={overviewImage2.description || ''}
                  enableFluidWidth={true}
                />
              }
            />
          </ModuleContainerStyleWrap>
        )}
        <HideOnMobile>
          <EoiInlineModule
            blueBackground={overviewText2Exists}
            eoiContainerText={eoiContainerText}
            eoiButtons={eoiButtons}
            trackingIndex={2}
            setEoiTrackingIndex={setEoiTrackingIndex}
          />
        </HideOnMobile>

        {upsellsExist && showFCNDModule && (
          <ModuleContainerStyleWrap>
            <FCNDModule />
          </ModuleContainerStyleWrap>
        )}

        {upsellsExist &&
          showUpsellsAboutModule &&
          upsellsAbout?.visual &&
          upsellsAbout.visual?.url &&
          upsellsAbout.visual?.width &&
          upsellsAbout.visual?.height && (
            <ModuleContainerStyleWrap>
              <UpsellsAboutModule
                title={upsellsAbout?.title || ''}
                description={upsellsAbout?.description || ''}
                ctaLabel={upsellsAbout?.callToActionLabel || ''}
                ctaLink={upsellsAbout?.callToActionLink || ''}
                image={
                  <LazyImgix
                    src={upsellsAbout.visual.url}
                    maxWidth={upsellsAbout.visual.width}
                    maxHeight={upsellsAbout.visual.height}
                    alt={upsellsAbout.visual.title || ''}
                    enableFluidWidth={true}
                  />
                }
              />
            </ModuleContainerStyleWrap>
          )}

        {!shouldMoveStackabilityModule && upsellsModule}

        {filteredEvents && !!filteredEvents.length && (
          <ModuleContainerStyleWrap>
            <EventsSection events={filteredEvents} />
          </ModuleContainerStyleWrap>
        )}
        {latestNews && (
          <ModuleContainerStyleWrap>
            <RelatedNewsModule relatedNewsContent={latestNews} />
          </ModuleContainerStyleWrap>
        )}
        {relatedToUpsellsExist && (
          <ModuleContainerStyleWrap>
            <UpsellsModule
              title={upsellsModuleCopy.relatedTo.title}
              description={upsellsModuleCopy.relatedTo.description}
              upsells={relatedToUpsells}
              trackingNamePrefix="related_to"
              trackingIndex={3}
            />
          </ModuleContainerStyleWrap>
        )}
        {faqCollection?.items && faqCollection.items.length > 0 && (
          <ModuleContainerStyleWrap>
            <FaqModule
              header={_t('Frequently asked questions')}
              faqItems={faqCollection.items}
              sideColumnContent={<HelpCenterBlock />}
              enableShowMoreList
            />
          </ModuleContainerStyleWrap>
        )}
        <HideOnMobile>
          <EoiInlineModule
            eoiContainerText={eoiContainerText}
            eoiButtons={eoiButtons}
            trackingIndex={3}
            setEoiTrackingIndex={setEoiTrackingIndex}
          />
        </HideOnMobile>
      </div>
    );
  }
};

export default OverviewPageContent;
